import { FormSelectOption } from "@carrotfertility/carotene-core-x"
import { ExtendedProvidersTypeaheadProvider } from "#/pages/provider-finder/types/Providers"
import {
  MemberProviderSelection,
  ProviderSelectionAction,
  UpsertMemberProviderSelectionRequest
} from "#/types/memberProviderSelection"
import { ProviderType } from "#/pages/provider-finder/utils/parentIdMapping"
import { PROVIDER_TYPE_SEARCHED } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { Journey } from "#/components/carrot-plans/types/journey"
import { JourneyStage } from "#/components/carrot-plans/types/journeyStage"
import { RoutingInfo } from "#/types/routingInfo"

// ============ Routing Info Patch Payload ============

/**
 * Typed payload for the routing-info PATCH endpoint, scoped to fields used by the provider steps.
 * Using this instead of Record<string, unknown> catches mistyped keys at compile time.
 */
export interface RoutingInfoProviderPatchPayload {
  skipFields?: string[]
  clearProviderSkipFields?: string[]
}

// ============ Constants ============

export const PROVIDER_CONSTANTS = {
  FERTILITY_CLINIC_PARENT_ID:
    Object.keys(PROVIDER_TYPE_SEARCHED).find((key) => PROVIDER_TYPE_SEARCHED[key] === ProviderType.FertilityClinics) ||
    "1",
  OBGYN_PROVIDER_TYPE: "OBGYN",
  NO_PROVIDER_RADIO_VALUE: "true",
  IVF_TREATMENT_START_ALREADY_HAD: "ALREADY_HAD",
  PRESERVATION_CYCLE_TESTING_ALREADY_DONE: "ALREADY_DONE"
}

// ============ Journey Stage Groups ============

/**
 * Groups of journey stages used for routing decisions.
 * Typed as string[] to allow direct string comparisons with journeyStage values from the API.
 * Uses JourneyStage enum values for maintainability and clarity.
 */
const JOURNEY_STAGES: {
  IUI_CYCLE: string[]
  GENERAL_PROVIDER_STEP: string[]
  ASSISTED_REPRODUCTION_NO_PROVIDER_ALL: string[]
  ASSISTED_REPRODUCTION_NO_PROVIDER_NON_US: string[]
  PRESERVATION_NO_PROVIDER: string[]
} = {
  IUI_CYCLE: [JourneyStage.ALREADY_STARTED_IUI_CYCLE, JourneyStage.RECENTLY_COMPLETED_IUI_CYCLE],
  GENERAL_PROVIDER_STEP: [
    JourneyStage.GETTING_READY_START_IUI,
    JourneyStage.EXPLORING,
    JourneyStage.MIGHT_WANT_IUI_IVF
  ],
  ASSISTED_REPRODUCTION_NO_PROVIDER_ALL: [JourneyStage.GETTING_READY_START_IVF],
  ASSISTED_REPRODUCTION_NO_PROVIDER_NON_US: [
    JourneyStage.EXPLORING,
    JourneyStage.MIGHT_WANT_IUI_IVF,
    JourneyStage.GETTING_READY_START_IUI
  ],
  PRESERVATION_NO_PROVIDER: [JourneyStage.EXPLORING, JourneyStage.PRESERVATION_READY]
}

// ============ Form Types ============

export interface FormValues {
  providerName: FormSelectOption | string | null
  provider: ExtendedProvidersTypeaheadProvider | null
  noProvider?: string
}

// ============ Provider Type Step Decision Logic ============

type ProviderTypeStepDecision = "SHOW_PROVIDER_TYPE_STEP_FOR_IUI" | "SHOW_PROVIDER_TYPE_STEP" | "GO_TO_CLINIC_NAME"

/**
 * Determines whether to show the "What kind of provider" step and which variant.
 * Used by WhatKindOfProviderDecisionStep to route to provider type step or clinic name step.
 *
 * @returns
 * - "SHOW_PROVIDER_TYPE_STEP_FOR_IUI": Show IUI-specific provider type selection
 * - "SHOW_PROVIDER_TYPE_STEP": Show general provider type selection
 * - "GO_TO_CLINIC_NAME": Skip provider type step, go directly to clinic name
 */
export function getProviderTypeStepDecision(
  isUsa: boolean,
  journeyType: string | null,
  journeyStage: string | null
): ProviderTypeStepDecision {
  const canShowProviderStep = isUsa && journeyType === Journey.ASSISTED_REPRODUCTION

  const isIuiCycleStep = journeyStage != null && JOURNEY_STAGES.IUI_CYCLE.includes(journeyStage)
  const shouldShowGeneral = journeyStage != null && JOURNEY_STAGES.GENERAL_PROVIDER_STEP.includes(journeyStage)

  if (canShowProviderStep && isIuiCycleStep) {
    return "SHOW_PROVIDER_TYPE_STEP_FOR_IUI"
  }
  if (canShowProviderStep && shouldShowGeneral) {
    return "SHOW_PROVIDER_TYPE_STEP"
  }
  return "GO_TO_CLINIC_NAME"
}

/**
 * Determines whether to show the "I don't have a provider yet" radio option.
 *
 * The option is shown based on:
 * - Journey type (Assisted Reproduction or Preservation)
 * - Journey stage
 * - User location (some stages only show for non-US users)
 * - Whether user selected "it's already done" for fertility testing (if so, always returns false)
 * - Whether user selected "it's already done" for preservation cycle testing (if so, always returns false)
 */
export function shouldShowClinicWithNoProviderOption(isUsa: boolean, routingInfo: RoutingInfo | null): boolean {
  if (!routingInfo?.journeyStage) return false

  const { journey, journeyStage, ivfTreatmentStart, preservationCycleTesting } = routingInfo

  // User already completed testing - they likely have a provider
  if (
    ivfTreatmentStart === PROVIDER_CONSTANTS.IVF_TREATMENT_START_ALREADY_HAD ||
    preservationCycleTesting === PROVIDER_CONSTANTS.PRESERVATION_CYCLE_TESTING_ALREADY_DONE
  ) {
    return false
  }

  const isAssistedReproduction = journey === Journey.ASSISTED_REPRODUCTION
  const isPreservation = journey === Journey.PRESERVATION

  if (isAssistedReproduction) {
    return (
      JOURNEY_STAGES.ASSISTED_REPRODUCTION_NO_PROVIDER_ALL.includes(journeyStage) ||
      (!isUsa && JOURNEY_STAGES.ASSISTED_REPRODUCTION_NO_PROVIDER_NON_US.includes(journeyStage))
    )
  }

  if (isPreservation) {
    return JOURNEY_STAGES.PRESERVATION_NO_PROVIDER.includes(journeyStage)
  }

  return false
}

// ============ Skip Request Builders (used by useClinicNameStepHandlers / useObGynNameStepHandlers) ============

/**
 * Builds the upsert request for when the user skips the clinic name step.
 * Clears providerEntityName and providerUuid. If user saw the provider type step (sawProviderTypeStep),
 * preserves providerType and sets action to HAS_PROVIDER; otherwise clears type and sets SKIPPED.
 */
export function createClinicSkipUpsertRequest(
  selection: MemberProviderSelection | null,
  routingInfoId: number
): UpsertMemberProviderSelectionRequest {
  const sawProviderTypeStep = selection?.sawProviderTypeStep ?? false
  const previousProviderType = selection?.providerType ?? null
  return {
    selectionSource: "ROUTING",
    providerType: sawProviderTypeStep ? previousProviderType : null,
    providerEntityName: undefined,
    providerUuid: undefined,
    providerSelectionAction: sawProviderTypeStep
      ? ProviderSelectionAction.HAS_PROVIDER
      : ProviderSelectionAction.SKIPPED,
    routingInfoId,
    sawProviderTypeStep: sawProviderTypeStep || undefined
  }
}

/**
 * Builds the upsert request for when the user skips the OBGYN name step.
 * Clears providerFirstName and providerLastName; preserves providerType, providerSelectionAction, and sawProviderTypeStep.
 */
export function createObGynSkipUpsertRequest(
  selection: MemberProviderSelection | null,
  routingInfoId: number
): UpsertMemberProviderSelectionRequest {
  return {
    selectionSource: "ROUTING",
    providerType: selection?.providerType ?? null,
    providerFirstName: undefined,
    providerLastName: undefined,
    providerSelectionAction: selection?.providerSelectionAction ?? ProviderSelectionAction.HAS_PROVIDER,
    routingInfoId,
    sawProviderTypeStep: selection?.sawProviderTypeStep || undefined
  }
}

// ============ Form Value Extraction & Sanitization ============

/**
 * Trims whitespace from a name input and returns null for empty/undefined values.
 * Used by both NameOfObGynStep and any other step that collects freeform name fields.
 *
 * @example
 * sanitizeNameInput("  John  ") // → "John"
 * sanitizeNameInput("")         // → null
 * sanitizeNameInput(undefined)  // → null
 */
export function sanitizeNameInput(value: string | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed || null
}

/**
 * Safely extracts a string value from FormSelectOption, string, or null.
 *
 * @example
 * extractStringValue("Mayo Clinic") // → "Mayo Clinic"
 * extractStringValue({ value: "Mayo Clinic", label: "Mayo Clinic" }) // → "Mayo Clinic"
 * extractStringValue(null) // → null
 */
export function extractStringValue(value: FormSelectOption | string | null): string | null {
  if (value == null) {
    return null
  }
  if (typeof value === "string") {
    return value
  }
  return String(value.value)
}

// ============ Provider Selection Action Logic ============

/**
 * Determines the provider selection action based on form values.
 * Preserves previous action only if user went through "what kind of provider" step.
 * Otherwise, uses the current form state to determine the action.
 *
 * Priority:
 * 1. Provider name entered → HAS_PROVIDER
 * 2. "No provider" selected → NO_PROVIDER
 * 3. Saw provider type step with previous action → preserve previous action
 * 4. Otherwise → SKIPPED
 *
 * @example
 * // User entered a provider name
 * determineProviderSelectionAction("Mayo Clinic", false, null, false) // → HAS_PROVIDER
 *
 * // User selected "no provider" radio
 * determineProviderSelectionAction(null, true, null, false) // → NO_PROVIDER
 *
 * // User saw provider type step and selected "no provider" there
 * determineProviderSelectionAction(null, false, "NO_PROVIDER", true) // → NO_PROVIDER
 */
function determineProviderSelectionAction(
  providerEntityName: string | null,
  noProvider: boolean,
  previousAction: ProviderSelectionAction | null,
  sawProviderTypeStep: boolean
): ProviderSelectionAction {
  if (providerEntityName?.trim()) {
    return ProviderSelectionAction.HAS_PROVIDER
  }

  if (noProvider) {
    return ProviderSelectionAction.NO_PROVIDER
  }

  if (sawProviderTypeStep && previousAction != null) {
    return previousAction
  }

  return ProviderSelectionAction.SKIPPED
}

// ============ Request Builders ============

/**
 * Determines the provider type for the request.
 * Returns null when there's no provider name (for "no provider" or "skipped" scenarios).
 * When provider name exists, preserves previous type if available, otherwise defaults to FertilityClinics.
 */
function determineProviderType(previousProviderType: string | null, providerEntityName: string | null): string | null {
  // If no provider name, always return null (for "no provider" or "skipped" scenarios)
  if (!providerEntityName?.trim()) {
    return null
  }
  // If provider name exists, preserve previous type if available, otherwise default to FertilityClinics
  if (previousProviderType) {
    return previousProviderType
  }
  return ProviderType.FertilityClinics
}

/**
 * Creates a provider selection request with the given parameters.
 * This is the low-level builder used by other request functions.
 *
 * Note: `sawProviderTypeStep` is used internally to determine the action logic (whether to preserve
 * previous action/type), but it is NOT included in the returned request. This is intentional:
 * `sawProviderTypeStep` is only set when the user goes through the "What kind of provider" step,
 * not when submitting the clinic name step. The value is read from existing data and used for
 * decision-making, but we don't update it here.
 */
function createProviderSelectionRequest(
  provider: ExtendedProvidersTypeaheadProvider | null,
  freeformName: string | null,
  noProvider: boolean,
  routingInfoId: number,
  previousAction: ProviderSelectionAction | null,
  previousProviderType: string | null,
  sawProviderTypeStep: boolean
): UpsertMemberProviderSelectionRequest {
  const providerEntityName = provider?.name || freeformName || null

  return {
    selectionSource: "ROUTING",
    providerUuid: provider?.uuid,
    providerEntityName,
    providerEligibilityStatusAtSelection: provider?.providerEligibilityStatus,
    routingInfoId,
    providerType: determineProviderType(previousProviderType, providerEntityName),
    providerSelectionAction: determineProviderSelectionAction(
      providerEntityName,
      noProvider,
      previousAction,
      sawProviderTypeStep
    )
  }
}

/**
 * Creates a request for when user skips the step (empty form submission).
 * Not part of the public API — exported only for unit testing.
 * Production code uses createClinicSkipUpsertRequest / createObGynSkipUpsertRequest via the step handler hooks.
 */
export function createSkipRequest(
  routingInfoId: number,
  previousAction: ProviderSelectionAction | null,
  previousProviderType: string | null,
  sawProviderTypeStep: boolean
): UpsertMemberProviderSelectionRequest {
  return createProviderSelectionRequest(
    null,
    null,
    false,
    routingInfoId,
    previousAction,
    previousProviderType,
    sawProviderTypeStep
  )
}

/**
 * Creates a request based on form values.
 * Handles all submission cases:
 * - "No provider" selected
 * - Provider selected from search results (has UUID)
 * - Freeform provider name entered
 * - Empty form (skip)
 *
 * @returns Request object, or null if routingInfoId is missing
 */
export function createFormSubmitRequest(
  values: FormValues,
  routingInfoId: number | null,
  previousAction: ProviderSelectionAction | null,
  previousProviderType: string | null,
  sawProviderTypeStep: boolean
): UpsertMemberProviderSelectionRequest | null {
  if (!routingInfoId) return null

  const noProvider = values.noProvider === PROVIDER_CONSTANTS.NO_PROVIDER_RADIO_VALUE
  const provider = values.provider?.uuid ? values.provider : null
  const freeformName = provider ? null : extractStringValue(values.providerName)?.trim() || null

  return createProviderSelectionRequest(
    provider,
    freeformName,
    noProvider,
    routingInfoId,
    previousAction,
    previousProviderType,
    sawProviderTypeStep
  )
}
